/* resources/assets/js/components/pages/results/results.vue */
/* resources/assets/js/institution/features/statisticsmanagement/TestResults.vue */

import uniq from 'ramda/es/uniq'
import moment from 'moment/min/moment.min'
import {TestResults, MainCategories, SubCategories, Answer} from 'Models'
import {ThemeMixin} from 'Mixins'

export default {
    mixins: [ThemeMixin],

    data() {
        return {
            currentMainCategoryIndex:   0,
            currentSubCategoryIndex:    0,
            currentAnswerIndex:         0,
            mainCategoryIds:            [],
            subCategoryIds:             [],
            detailContextMainCategoryId: null,
            detailContextSubCategoryId: null,
        }
    },

    computed: {
        actualDurationInSeconds()   {
            const diff      = (end,start) => moment(end) - moment(start)
            const inSecs    = x => moment.duration(x).asSeconds()
            return inSecs( diff(this.endTime,this.startTime) )
        },

        mainCategory()      { return this.mainCategories.find( mc => mc.id()===this.selectedMainCategoryId ) },
        subCategory()       { return this.subCategories.find( sc => sc.id()==this.selectedSubCategoryId ) },

        selectedMainCategoryId()    { return this.mainCategoryIds[this.currentMainCategoryIndex] },
        selectedSubCategoryId()     { return this.subCategoryIds[this.currentSubCategoryIndex] },
        selectedAnswer()            { return this.userAnswers[this.currentAnswerIndex] },
    },

    methods: {
        setDetailContext(mainCategoryID, subCategoryID) {
            this.detailContextMainCategoryId = Number(mainCategoryID || 0) || null
            this.detailContextSubCategoryId = Number(subCategoryID || 0) || null
        },

        applyDetailContextSelection() {
            if (!this.detailContextMainCategoryId || !this.detailContextSubCategoryId)
                return

            MainCategories.selectById(this.detailContextMainCategoryId)
            SubCategories.selectById(this.detailContextSubCategoryId)
        },

        applyAnswerContextSelection(answer) {
            if (!answer) return

            const mainCategoryID = answer.mainCategoryId()
            const subCategoryID = answer.resultsSubCategoryId()

            MainCategories.selectById(mainCategoryID)
            SubCategories.selectById(subCategoryID)
        },

        syncCategoryContextFromCurrentAnswer() {
            const answer = this.userAnswers[this.currentAnswerIndex]
            if (!answer) return

            const mainCategoryID = answer.mainCategoryId()
            const subCategoryID = answer.resultsSubCategoryId()

            this.setDetailContext(mainCategoryID, subCategoryID)
            this.applyDetailContextSelection()

            const mainCategoryIndex = this.mainCategoryIds.findIndex((id) => id === mainCategoryID)
            if (mainCategoryIndex >= 0) {
                this.currentMainCategoryIndex = mainCategoryIndex
            }

            this.setSubCategoryIdsFor(mainCategoryID)

            const subCategoryIndex = this.subCategoryIds.findIndex((id) => id == subCategoryID)
            if (subCategoryIndex >= 0) {
                this.currentSubCategoryIndex = subCategoryIndex
            }
        },

        findSubCategoriesFor(mainCategoryId) {
            return this.userAnswers.filter( ua => ua.mainCategoryId()==mainCategoryId)
                .map( ua => ua.resultsSubCategoryId() )
        },

        setSubCategoryIdsFor(mainCategoryId) {
            this.subCategoryIds  = uniq(this.findSubCategoriesFor(mainCategoryId))
        },

        initCategoryStructure() {
            this.mainCategoryIds = uniq(this.userAnswers.map(ua => ua.mainCategoryId()))
            this.setSubCategoryIdsFor(this.mainCategoryIds[0])
        },


        onMainCategoryPageItemClicked(itemID, goToNextView=true) {
            this.currentMainCategoryIndex = this.mainCategoryIds.findIndex( id => id===itemID)
            MainCategories.selectById(itemID)
            this.setSubCategoryIdsFor(itemID)

            if(goToNextView) this.handleGoToNextView()
        },

        onSubCategoryPageItemClicked(itemID, goToNextView=true) {
            this.currentSubCategoryIndex = this.subCategoryIds.findIndex( id => id==itemID);
            SubCategories.selectById(itemID)

            if(goToNextView) this.handleGoToNextView()
        },

        onSummaryPageItemClicked(itemID, goToNextView=true) {
            const selectedMainCategory = MainCategories.getSelected()
            const selectedSubCategory = SubCategories.getSelected()
            const summaryContextMainCategoryId = selectedMainCategory && typeof selectedMainCategory.id === 'function'
                ? selectedMainCategory.id()
                : this.selectedMainCategoryId
            const summaryContextSubCategoryId = selectedSubCategory && typeof selectedSubCategory.id === 'function'
                ? selectedSubCategory.id()
                : this.selectedSubCategoryId

            this.currentAnswerIndex = this.userAnswers.findIndex( ua => ua.id()==itemID)

            const answer = this.userAnswers[this.currentAnswerIndex]
            if (answer) {
                const answerMainCategoryId = answer.mainCategoryId()
                const answerSubCategoryId = answer.resultsSubCategoryId()

                const mainCategoryIndex = this.mainCategoryIds.findIndex((id) => id === answerMainCategoryId)
                if (mainCategoryIndex >= 0) {
                    this.currentMainCategoryIndex = mainCategoryIndex
                } else {
                    this.currentMainCategoryIndex = 0
                }

                this.setSubCategoryIdsFor(answerMainCategoryId)

                const subCategoryIndex = this.subCategoryIds.findIndex((id) => id == answerSubCategoryId)
                if (subCategoryIndex >= 0) {
                    this.currentSubCategoryIndex = subCategoryIndex
                } else {
                    this.currentSubCategoryIndex = 0
                }

                const stableSummaryMainCategoryID = summaryContextMainCategoryId || answerMainCategoryId
                const stableSummarySubCategoryID = summaryContextSubCategoryId || answerSubCategoryId
                this.setDetailContext(stableSummaryMainCategoryID, stableSummarySubCategoryID)

                this.applyDetailContextSelection()
            }

            if(goToNextView) this.handleGoToNextView()
        },

        onQuestionChangedTo(id) {
            const qID = Answer.findIndexById(id)
            Answer.setSelectedUserAnswerIndex(qID)
        },

        handleGoToPrevQuestion() {
            if(this.isMainCategoriesResultsView) return

            if(this.isSubCategoriesResultsView && this.currentMainCategoryIndex>0) {
                let mainCategoryId = this.mainCategoryIds[--this.currentMainCategoryIndex];
                this.onMainCategoryPageItemClicked(mainCategoryId, false);
            }

            if(this.isSummaryResultsView && this.currentSubCategoryIndex>0) {
                let subCategoryId = this.subCategoryIds[--this.currentSubCategoryIndex];
                this.onSubCategoryPageItemClicked(subCategoryId, false);
            }

            if(this.isDetailsResultView && this.currentAnswerIndex>0) {
                --this.currentAnswerIndex;

                this.syncCategoryContextFromCurrentAnswer()
            }

            this.handleSetAnswerIndex()
        },

        handleGoToNextQuestion() {
            if(this.isMainCategoriesResultsView) return

            if(this.isSubCategoriesResultsView && this.currentMainCategoryIndex<(this.mainCategoryIds.length-1)) {
                let mainCategoryId = this.mainCategoryIds[++this.currentMainCategoryIndex];
                this.onMainCategoryPageItemClicked(mainCategoryId, false);
            }

            if(this.isSummaryResultsView && this.currentSubCategoryIndex<(this.subCategoryIds.length-1)) {
                let subCategoryId = this.subCategoryIds[++this.currentSubCategoryIndex];
                this.onSubCategoryPageItemClicked(subCategoryId, false);
            }

            if(this.isDetailsResultView && this.currentAnswerIndex<(this.userAnswers.length-1)) {
                ++this.currentAnswerIndex;

                this.syncCategoryContextFromCurrentAnswer()
            }


            this.handleSetAnswerIndex()
        },

        handleGoBack()          {
            TestResults.setToPreviousResultsViewMode(this.showCategoryViews)
            this.handleSetAnswerIndex()
        },
        handleGoToNextView()    {
            TestResults.setToNextResultsViewMode(this.showCategoryViews)
            this.handleSetAnswerIndex()
        },

        // IMPORTANT NOTE:  Setting the user answer index is necessary to be able to understand,
        //                  whether or not a user is in the beginning/end of an area
        // UseCase:         Show/Hide Prev/Next button.
        // Refactoring:     Implement in vuex like actual questions and answers, so
        //                  that it is easier, cleaner and persistent across page
        //                  refreshs.
        handleSetAnswerIndex() {
            if(this.isSubCategoriesResultsView) {
                if(this.currentMainCategoryIndex === 0)
                    Answer.setSelectedUserAnswerIndex(0)
                else if(this.currentMainCategoryIndex >= (this.mainCategoryIds.length-1))
                    Answer.setSelectedUserAnswerIndex(Answer.userAnswersCount()-1)
                else
                    Answer.setSelectedUserAnswerIndex(1)
            }

            if(this.isSummaryResultsView) {
                if(this.currentSubCategoryIndex === 0)
                    Answer.setSelectedUserAnswerIndex(0)
                else if(this.currentSubCategoryIndex >= (this.subCategoryIds.length-1))
                    Answer.setSelectedUserAnswerIndex(Answer.userAnswersCount()-1)
                else
                    Answer.setSelectedUserAnswerIndex(1)
            }
        },
    }
};

